import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowRight, Building2, GraduationCap, Trophy, Users, FileText, ExternalLink, Megaphone, Link2, Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { usePoly } from "./PolyLayout";
import { STATIC_CONTENT } from "./_shared";
import RecentAcademicsWidget from "@/components/RecentAcademicsWidget";
import AlumniStrip from "@/components/AlumniStrip";

function Slider({ slides }) {
  const [idx, setIdx] = useState(0);
  const list = slides.length > 0 ? slides : null;
  useEffect(() => {
    if (!list || list.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), 5000);
    return () => clearInterval(t);
  }, [list]);

  if (!list) {
    return (
      <section className="relative bg-gradient-to-br from-[#003C6C] via-[#003C6C] to-[#0a4d8f] text-white overflow-hidden" data-testid="poly-hero">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute -top-20 right-10 w-80 h-80 rounded-full bg-[#FDC700] blur-3xl" />
          <div className="absolute bottom-0 -left-10 w-96 h-96 rounded-full bg-pink-500 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 text-center">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#FDC700] font-semibold">
            Future-Ready Engineering
          </div>
          <h1 className="mt-3 font-serif text-5xl md:text-7xl tracking-tight">MIT Institute of Emerging Science and Technology</h1>
          <p className="mt-4 text-base md:text-xl text-white/85 max-w-2xl mx-auto">
            Established 2024 · AICTE Approved · Next-generation programmes in AI,
            robotics, cyber security & sustainable technology.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="admissions"
              className="bg-[#FDC700] hover:bg-[#FFD93D] text-slate-900 font-semibold px-6 py-3 rounded-md inline-flex items-center gap-2"
            >
              Admissions <ArrowRight size={16} strokeWidth={2} />
            </Link>
            <Link
              to="about"
              className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-6 py-3 rounded-md"
            >
              About Us
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative bg-black" data-testid="poly-hero-slider">
      <div className="relative w-full overflow-hidden" style={{ aspectRatio: "1920 / 620" }}>
        {list.map((s, i) => (
          <a
            key={s.id}
            href={s.link_url || undefined}
            target={s.link_url ? "_blank" : undefined}
            rel="noopener noreferrer"
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === idx ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <img src={s.image_url} alt={s.caption || ""} className="w-full h-full object-cover" />
            {s.caption && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-6 md:p-12">
                <div className="max-w-7xl mx-auto">
                  <h2 className="font-serif text-white text-2xl md:text-5xl drop-shadow">
                    {s.caption}
                  </h2>
                </div>
              </div>
            )}
          </a>
        ))}
        {list.length > 1 && (
          <>
            <button
              onClick={() => setIdx((i) => (i - 1 + list.length) % list.length)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/40 hover:bg-[#FDC700] hover:text-slate-900 text-white flex items-center justify-center"
              aria-label="Previous"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => setIdx((i) => (i + 1) % list.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/40 hover:bg-[#FDC700] hover:text-slate-900 text-white flex items-center justify-center"
              aria-label="Next"
            >
              <ChevronRight size={20} />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {list.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-2 rounded-full transition-all ${
                    i === idx ? "bg-[#FDC700] w-6" : "bg-white/40 hover:bg-white/70 w-2"
                  }`}
                  aria-label={`slide ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

const STATS = [
  { icon: GraduationCap, n: "2024", l: "Year of Inception", color: "from-[#FDC700] to-[#FFB300]" },
  { icon: Users, n: "8+", l: "Emerging Tech Streams", color: "from-pink-500 to-rose-500" },
  { icon: Building2, n: "AICTE", l: "Approved Institution", color: "from-teal-500 to-blue-600" },
  { icon: Trophy, n: "Industry 4.0", l: "Future-Ready Labs", color: "from-purple-500 to-indigo-600" },
];

// Convert any YouTube watch/share/short/embed URL to an embed URL
function toYoutubeEmbed(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    let id = "";
    if (host === "youtu.be") {
      id = u.pathname.split("/").filter(Boolean)[0] || "";
    } else if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      if (u.pathname === "/watch") {
        id = u.searchParams.get("v") || "";
      } else if (u.pathname.startsWith("/embed/")) {
        id = u.pathname.split("/")[2] || "";
      } else if (u.pathname.startsWith("/shorts/")) {
        id = u.pathname.split("/")[2] || "";
      }
    }
    return id ? `https://www.youtube.com/embed/${id}?rel=0` : "";
  } catch {
    return "";
  }
}

export default function Home() {
  const data = usePoly();
  const academics = data.academics || [];
  const inst = data.institute || {};
  const admissions = data.notices_admission || [];
  const links = data.links || [];
  const notices = data.notices_general || [];
  const ytEmbed = toYoutubeEmbed(inst.youtube_url);

  return (
    <div data-testid="poly-home">
      <Slider slides={data.header_slides} />

      {/* Stat strip */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-5">
          {STATS.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.l}
                className="flex items-center gap-3"
              >
                <div className={`w-12 h-12 rounded-md bg-gradient-to-br ${s.color} text-white flex items-center justify-center`}>
                  <Icon size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-serif text-xl md:text-2xl text-slate-900">{s.n}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide">{s.l}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* About teaser */}
      <section className="py-14 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#003C6C] font-semibold">
              Welcome to MIT Institute of Emerging Science and Technology
            </div>
            <h2 className="mt-2 font-serif text-3xl md:text-5xl text-slate-900">
              Building technologists for <span className="text-[#003C6C]">tomorrow</span>
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {STATIC_CONTENT.about_mit.split(". ").slice(0, 3).join(". ") + "."}
            </p>
            <Link
              to="about"
              className="mt-6 inline-flex items-center gap-2 bg-[#003C6C] hover:bg-[#002a4d] text-white px-5 py-2.5 rounded-md text-sm"
            >
              Read more <ArrowRight size={14} strokeWidth={2} />
            </Link>
          </div>
          <div
            className="rounded-xl overflow-hidden shadow-xl ring-1 ring-slate-900/10 bg-black"
            data-testid="poly-home-video"
          >
            <div className="relative w-full aspect-video">
              {ytEmbed ? (
                <iframe
                  src={ytEmbed}
                  title={`${inst.name || "Institute"} — Campus Story`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                  data-testid="poly-home-youtube"
                />
              ) : inst.home_image_url ? (
                <img
                  src={inst.home_image_url}
                  alt={inst.name || "Institute"}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  data-testid="poly-home-image"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">
                  Video / image will appear here once configured by the institute.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Admissions / Important Links / Notices — side by side */}
      <section className="py-14 md:py-20 bg-white" data-testid="poly-home-info-strip">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#003C6C] font-semibold">
              Stay Informed
            </div>
            <h2 className="mt-2 font-serif text-3xl md:text-5xl text-slate-900">
              Latest Updates
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <InfoColumn
              title="Admissions"
              eyebrow="Join Us"
              icon={Megaphone}
              gradient="from-rose-500 to-orange-500"
              ringColor="ring-rose-100"
              items={admissions}
              empty="Admission notices will appear here once published."
              testid="poly-home-admissions"
            />
            <InfoColumn
              title="Important Links"
              eyebrow="Resources"
              icon={Link2}
              gradient="from-teal-500 to-blue-600"
              ringColor="ring-teal-100"
              items={links}
              empty="No important links have been published yet."
              testid="poly-home-links"
            />
            <InfoColumn
              title="Notices"
              eyebrow="Communications"
              icon={Bell}
              gradient="from-[#003C6C] to-[#0a4d8f]"
              ringColor="ring-[#FFF1AA]"
              items={notices}
              empty="No notices have been published yet."
              testid="poly-home-notices"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gradient-to-r from-[#FDC700] to-[#FFB300] text-slate-900">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl">Admissions 2026-27 open</h2>
            <p className="mt-1 text-slate-800/90">
              UG Programmes in AI, Data Science, Robotics, Cyber Security & more
            </p>
            <p className="mt-1 text-slate-800/90">
              Approved by AICTE New Delhi · Industry-aligned curriculum
            </p>
          </div>
          <Link
            to="departments"
            className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-md inline-flex items-center gap-2 font-medium"
          >
            Explore Departments <ArrowRight size={16} strokeWidth={2} />
          </Link>
        </div>
      </section>
    <RecentAcademicsWidget docs={academics} theme="yellow" testidPrefix="mitest-ac" linkTo="academics" />
    <AlumniStrip items={(data.alumni || []).filter((a) => a.featured)} theme="light" testid="mitest-alumni" registerHref="/mitest/alumni/register" />
    </div>
  );
}

function InfoColumn({ title, eyebrow, icon: Icon, gradient, ringColor, items, empty, testid }) {
  const list = items.slice(0, 8);
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const isNew = (it) => {
    const ts = it.created_at ? new Date(it.created_at).getTime() : NaN;
    return Number.isFinite(ts) && ts >= sevenDaysAgo;
  };
  return (
    <div
      data-testid={testid}
      className={`bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-lg transition-shadow ring-4 ${ringColor} flex flex-col`}
    >
      <div className={`bg-gradient-to-r ${gradient} text-white rounded-t-xl px-5 py-4 flex items-center gap-3`}>
        <div className="w-10 h-10 rounded-md bg-white/15 flex items-center justify-center">
          <Icon size={20} strokeWidth={1.5} />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/80 font-semibold">
            {eyebrow}
          </div>
          <div className="font-serif text-xl leading-tight">{title}</div>
        </div>
      </div>
      <div className="p-4 flex-1">
        {list.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-10">{empty}</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {list.map((it) => (
              <li
                key={it.id}
                className="py-3 flex items-start gap-3"
                data-testid={`${testid}-item-${it.id}`}
              >
                <FileText size={14} strokeWidth={1.5} className="text-[#003C6C] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800 line-clamp-2 flex items-start gap-1.5">
                    <span className="flex-1">{it.name}</span>
                    {isNew(it) && (
                      <span
                        data-testid={`${testid}-new-badge-${it.id}`}
                        className="shrink-0 inline-flex items-center text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-rose-500 text-white animate-pulse"
                      >
                        New
                      </span>
                    )}
                  </div>
                  {(it.pdf_url || it.external_url) && (
                    <a
                      href={it.pdf_url || it.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-0.5 inline-flex items-center gap-1 text-xs text-[#003C6C] hover:text-[#003C6C] font-medium"
                    >
                      Open <ExternalLink size={10} strokeWidth={1.5} />
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>    </div>
  );
}
